import React, { useCallback, useEffect } from "react";
import Nav from "../../components/Nav/Nav";

import {UserContext} from "../../App";
import { Button, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Paper, CircularProgress, Box } from "@mui/material";

import './HomeView.scss';
import { Link, useNavigate } from "react-router-dom";
import { FullProsecutorDto } from "../../api/ProsecutorApi";
import { FullCourtDto } from "../../api/CourtApi";
import { UserDto } from "../../interfaces/UserInterfaces";
import { getStatusColor } from "../../utils/GetStatusColor";
import { fetchTicketsPage, TICKETS_PAGE_SIZE } from "../../api/fetchTicketsPage";

export interface TicketDto {
  Ticket_guid: string;
  Ticket_Number: string;
  Ticket_MatterNumber: string | null;
  Ticket_Address: string;
  Ticket_CourtDate: string;
  Ticket_CourtDateManualOverride?: boolean;
  Ticket_Citation: string;
  Ticket_Description: string | null;
  Ticket_OffenseDate: string | null;
  // Printed on the two documents that go out with a not-guilty letter (Lloyd, Aug 17 2026).
  Ticket_CauseNumber: string | null;
  Ticket_AgencyCaseNumber: string | null;
  Ticket_LicensePlate: string | null;
  Ticket_ArrestingAgency: string | null;
  Ticket_ArrestingAgencyAddress: string | null;
  Ticket_LorTemplateOverride: string | null;
  Ticket_RespondByDate: string | null;
  Ticket_JuryTrialDate: string | null;
  // The discovery record (Lloyd, Aug 19). The two dates fill the DS and DR boxes on the case
  // journey; the location is a DiscoveryLocation code — see constants/discoveryLocations.ts.
  Ticket_DiscoveryRequestedAt: string | null;
  Ticket_DiscoveryReceivedAt: string | null;
  Ticket_DiscoveryLocation: string | null;
  Ticket_DiscoveryNote: string | null;
  Ticket_City: string;
  Ticket_County: string;
  Ticket_LicenseType: string;
  Ticket_Representation: string;
  Ticket_Zone: string;
  Ticket_Url: string;
  Ticket_CreatedAt: string;
  Ticket_Active: boolean;
  Ticket_DriversLicenseNumber?: string | null;
  Ticket_Gender?: string | null;
  Ticket_PronounSet?: string | null;
  Ticket_HasTakenDscInPastYear?: boolean | null;
  Ticket_LorSent?: boolean;
  Ticket_LorSentAt?: string | null;
  Ticket_LorDay3SentAt?: string | null;
  Ticket_LorDay7SentAt?: string | null;
  Ticket_LorDay10SentAt?: string | null;
  Ticket_LorConfirmedAt?: string | null;
  Ticket_LorEscalationStage?: number;
  /**
   * 'TRAFFIC' | 'NON_TRAFFIC'. Undefined or null on cases that pre-date the field, which are all
   * traffic. Anything traffic-specific — the progression bar, stale flagging — checks this first.
   */
  Ticket_MatterType?: string | null;
}

export interface CourtEventDto {
  CourtEvent_guid: string;
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title: string;
  CourtEvent_Desc: string;
}

export interface ContextTicketDto {
  ticket: TicketDto;
  ticketStatus: TicketStatusDto | null;
  statusHistory: TicketStatusDto[];
  prosecutor?: FullProsecutorDto | null;
  court?: FullCourtDto | null;
  nextCourtEvent?: CourtEventDto | null;
  user?: UserDto | null;
  /** Server-classified usability of the court's email address — see backend courtEmailRouting. */
  courtEmailStatus?: 'valid' | 'placeholder' | 'missing' | null;
  /**
   * Which letter of representation this case routes to, and why. Decided on the server so the
   * screen and the generated letter cannot disagree about the rule.
   */
  lorRouting?: {
    templateType: string;
    reason: string;
    needsStaffReview: boolean;
    age: number | null;
    /** True when a manager picked the letter by hand instead of the rules choosing it. */
    isManualOverride: boolean;
    /** Recorded offense date, or null on cases that predate the field. */
    offenseDate: string | null;
    /** What the rules would choose with no override — labels the "choose automatically" option. */
    automaticTemplateType: string;
    /** Which date the age rule read; null when it had nothing usable or rejected an import sentinel. */
    citationAgeSource: 'offense-date' | 'court-date' | null;
  } | null;
}

export interface CreateTicketDto {
  Ticket_CreatedAt: string;
}

export interface TicketStatusDto {
  TicketStatus_guid: string;
  TicketStatus_DateTime: string;
  TicketStatus_Name: TicketStatusNameType;
  TicketStatus_Desc: string;
}

export const ticketStatusNames: TicketStatusNameType[] = [
  'citation received (Approving)',
  'case accepted (Approved)',
  'letter of representation sent (Active)',
  'court respond by date satisfied (Pending)',
  'New court date',
  'negotiating with prosecutor',
  'offer received',
  'waiting for client to reject or accept offer',
  'waiting for judge to approve',
  'deferral/DSC conditions',
  'case closed/dismissed (closed)'
];
export type TicketStatusNameType = 
  'citation received (Approving)' |
  'case accepted (Approved)' |
  'letter of representation sent (Active)' |
  'court respond by date satisfied (Pending)' |
  'New court date' |
  'negotiating with prosecutor' |
  'offer received' |
  'waiting for client to reject or accept offer' |
  'waiting for judge to approve' |
  'deferral/DSC conditions' |
  'case closed/dismissed (closed)';

const HomeView = () => {
  const user = React.useContext(UserContext);
  const navigate = useNavigate();

  const [activeTickets, setActiveTickets] = React.useState<ContextTicketDto[]>([]);
  const [ticketsLoading, setTicketsLoading] = React.useState(true);
  const [ticketsLoadingMore, setTicketsLoadingMore] = React.useState(false);
  const [ticketsCurrentPage, setTicketsCurrentPage] = React.useState(0);
  const [ticketsHasMore, setTicketsHasMore] = React.useState(false);

  const handleTicketClick = (ticketGuid: string) => {
    navigate(`/ticket?Ticket_guid=${ticketGuid}`);
  };

  const loadTickets = useCallback(async (page = 0, appendResults = false) => {
    if (!appendResults) {
      setTicketsLoading(true);
    } else {
      setTicketsLoadingMore(true);
    }

    try {
      const { tickets, hasMore } = await fetchTicketsPage({
        from: page * TICKETS_PAGE_SIZE,
        size: TICKETS_PAGE_SIZE,
      });

      if (appendResults) {
        setActiveTickets((prev) => [...prev, ...tickets]);
      } else {
        setActiveTickets(tickets);
      }

      setTicketsHasMore(hasMore);
      setTicketsCurrentPage(page);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setTicketsLoading(false);
      setTicketsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadTickets(0, false);
  }, [loadTickets]);

  const handleLoadMoreTickets = () => {
    loadTickets(ticketsCurrentPage + 1, true);
  };

  return (
    <>
      <Nav></Nav>
      <div className="homev">
        <Container className="homev-ctn">
          <Typography variant="h1" className="homev-ctn-ttl">Welcome {user?.User_FirstName}</Typography>
          <Typography>Texas Ticket Doctor is your personal assistant for managing traffic violations.</Typography>
          {ticketsLoading ? (
            <>
              <br />
              <Typography variant="h5" className="">Your Active Tickets</Typography>
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading tickets...
                </Typography>
              </Paper>
            </>
          ) : activeTickets.length > 0 && (
            <>
              <br />
              <Typography variant="h5" className="">Your Active Tickets</Typography>
              <TableContainer component={Paper}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {activeTickets.length} ticket{activeTickets.length === 1 ? '' : 's'}
                    {ticketsHasMore ? ' (more available)' : ''}
                  </Typography>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket Number</TableCell>
                      <TableCell>Citation</TableCell>
                      <TableCell>Representation</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Court Date</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTickets.map((contextTicket) => (
                      <TableRow
                        key={contextTicket.ticket.Ticket_guid}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(contextTicket.ticket.Ticket_guid)}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {contextTicket.ticket.Ticket_Number}
                          </Typography>
                        </TableCell>
                        <TableCell>{contextTicket.ticket.Ticket_Citation}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {contextTicket.ticket.Ticket_Representation || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {contextTicket.ticketStatus ? (
                            <Chip
                              label={contextTicket.ticketStatus.TicketStatus_Name}
                              color={getStatusColor(contextTicket.ticketStatus.TicketStatus_Name as TicketStatusNameType)}
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="approving"
                              color="warning"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(contextTicket.ticket.Ticket_CourtDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(contextTicket.ticket.Ticket_CreatedAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {ticketsHasMore && (
                  <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      variant="outlined"
                      onClick={handleLoadMoreTickets}
                      disabled={ticketsLoadingMore}
                      startIcon={ticketsLoadingMore ? <CircularProgress size={20} /> : undefined}
                    >
                      {ticketsLoadingMore
                        ? 'Loading...'
                        : `Load More (Showing ${activeTickets.length} results)`}
                    </Button>
                  </Box>
                )}
              </TableContainer>
            </>
          )}
          <br />
          <Button component={Link} to="/new-ticket" variant="contained" color="primary">Submit a new ticket</Button>
        </Container>
      </div>
    </>
  );
}
export default HomeView;
